import { Dashboard } from '@/components/looker/dashboard';
import type { Alert } from '@/entities/alert';
import { AlertType } from '@/entities/alert-type';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
// Legacy warranty imports
import { WarrantyConditionPreview as LegacyWarrantyConditionPreview } from '@/warranty/view/components/previews/warranty-condition-preview';
import { WarrantyPenaltyPreview as LegacyWarrantyPenaltyPreview } from '@/warranty/view/components/previews/warranty-penalty-preview';

import { AlertActions } from './alert-actions';
// Warranty V3 imports
import { WarrantyConditionPreview as V3WarrantyConditionPreview } from './warranty-condition-preview/warranty-condition.preview';
import { WarrantyMetricPreview as V3WarrantyMetricPreview } from './warranty-metric-preview/warranty-metric.preview';
import { WarrantyPenaltyPreview as V3WarrantyPenaltyPreview } from './warranty-penalty-preview/warranty-penalty.preview';

// Map specific alert types to alternative Looker dashboard preview IDs.
// If a type is not listed here, it will default to `${type}_preview`.
const DASHBOARD_PREVIEW_BY_TYPE: Readonly<Record<string, string>> = {
  // inactivity: 'performance_preview',
};

export type AlertDetailsProps = {
  alert?: Alert;
};

export function AlertDetails({ alert }: AlertDetailsProps) {
  const featureFlag = useFeatureFlag();

  if (!alert) {
    return (
      <div className="text-md flex items-center justify-center font-light text-gray-400/80">
        <p>Select an alert</p>
      </div>
    );
  }

  const { type, fullFrontendId } = alert;
  const dashboardId = DASHBOARD_PREVIEW_BY_TYPE[type] ?? `${type}_preview`;

  if (alert.type === AlertType.WARRANTY_CONDITION) {
    return featureFlag('warrantyV3') ? (
      <V3WarrantyConditionPreview alert={alert} />
    ) : (
      <LegacyWarrantyConditionPreview alert={alert} />
    );
  }
  if (alert.type === AlertType.WARRANTY_PENALTY) {
    return featureFlag('warrantyV3') ? (
      <V3WarrantyPenaltyPreview alert={alert} />
    ) : (
      <LegacyWarrantyPenaltyPreview alert={alert} />
    );
  }
  if (
    alert.type === AlertType.WARRANTY_RTE ||
    alert.type === AlertType.WARRANTY_SOH ||
    alert.type === AlertType.WARRANTY_TAG ||
    alert.type === AlertType.WARRANTY_TAG_PCS
  ) {
    return featureFlag('warrantyV3') ? (
      // @ts-expect-error it's the good alert.type but it's not typed correctly
      <V3WarrantyMetricPreview alert={alert} />
    ) : null;
  }

  return (
    <div data-testid="alert-details">
      <Dashboard
        id={dashboardId}
        key={`${type}-${fullFrontendId}`}
        filters={{ full_frontend_id: fullFrontendId }}
        actions={<AlertActions alert={alert} />}
      />
    </div>
  );
}
